// Web CRM (Business plane) — Scheduling: create-booking + config-authoring i18n (STATIC CHROME).
// Same flat-key + English-fallback semantics; merged into the shared per-language lookup.
// Reuses sched.policy.* / sched.req.* / common.* where they already exist (see schedulingTranslations).
// Backing endpoints (real, in the scheduling backend candidate):
//   POST /api/v1/scheduling/appointments        (staff-initiated create — customer resolved server-side)
//   GET  /api/v1/scheduling/availability         (real slots — never invented)
//   GET  /api/v1/scheduling/config               (read tenant scheduling config)
//   PUT  /api/v1/scheduling/config               (management authoring)

const schedulingExtraTranslations = {
  en: {
    // ---- create booking ----
    'sched.create.button': 'New booking',
    'sched.create.title': 'New appointment',
    'sched.create.type': 'Appointment type',
    'sched.create.providerFilter': 'Provider (optional filter)',
    'sched.create.loadSlots': 'Load available times',
    'sched.create.loadingSlots': 'Loading available times…',
    'sched.create.noSlots': 'No available times — configure availability first.',
    'sched.create.noCrew': 'No eligible staff — assign later.',
    'sched.create.pickSlot': 'Choose a time',
    'sched.create.customerId': 'Customer ID',
    'sched.create.phone': 'Phone',
    'sched.create.email': 'Email',
    'sched.create.customerHint': 'Provide at least one: Customer ID, phone or email. The server resolves the customer.',
    'sched.create.reason': 'Reason (optional)',
    'sched.create.submit': 'Create booking',
    'sched.create.needType': 'Select an appointment type.',
    'sched.create.needCustomer': 'Provide a Customer ID, phone or email.',
    // GOV-7 Issue B — profile-driven New-Booking modal (customer typeahead, resource label, pickup/destination, consent)
    'sched.create.customer': 'Customer',
    'sched.create.customerSearch': 'Search customer by name, phone or email',
    'sched.create.customerSearchPlaceholder': 'Type a name, phone or email…',
    'sched.create.searching': 'Searching…',
    'sched.create.noCustomers': 'No matching customer — add a new one by phone/email below.',
    'sched.create.searchFailed': 'Unable to search customers. Try again.',
    'sched.create.newCustomerHint': 'Or add a new customer by phone/email below.',
    'sched.create.pickup': 'Pickup',
    'sched.create.destination': 'Destination',
    'sched.create.needPickup': 'Enter the pickup location.',
    'sched.create.needDestination': 'Enter the destination.',
    'sched.create.consentRequired': 'Location consent required',
    'sched.create.consentHint': 'Consent is obtained from the customer directly (explicit grant). Staff cannot grant it here.',
    'sched.toast.created': 'Appointment created',
    // ---- config authoring ----
    'nav.schedulingSettings': 'Scheduling Settings',
    'navdesc.schedulingSettings': 'Hours, types, providers, policies',
    'page.schedConfig.title': 'Scheduling Settings',
    'page.schedConfig.subtitle': 'Configure availability, types, providers and policies for {business}',
    'schedcfg.loading': 'Loading configuration…',
    'schedcfg.failedLoad': 'Failed to load configuration',
    'schedcfg.none': 'No scheduling configuration yet — set it up below.',
    // Initial setup from the tenant's assigned Industry Profile. Business-facing wording only: no
    // pack id, profile key, registry or engine term is ever surfaced to a Business user.
    'schedcfg.setup.cta': 'Set up from Industry Profile',
    'schedcfg.setup.done': 'Scheduling set up from your Industry Profile',
    'schedcfg.setup.failed': 'Could not set up scheduling',
    'schedcfg.setup.noTemplate': 'No industry setup is available for this business yet',
    'schedcfg.save': 'Save configuration',
    'schedcfg.saved': 'Configuration saved',
    'schedcfg.saveFailed': 'Failed to save configuration',
    // ---- D5 appointment reminders ----
    'schedcfg.reminders.title': 'Appointment reminders',
    'schedcfg.reminders.enabled': 'Send appointment reminders',
    'schedcfg.reminders.offsets': 'When to remind (before the appointment)',
    'schedcfg.reminders.o72': '72 hours before',
    'schedcfg.reminders.o24': '24 hours before',
    'schedcfg.reminders.o1': '1 hour before',
    'schedcfg.reminders.channels': 'Reminder channels',
    'schedcfg.reminders.chInApp': 'In-app',
    'schedcfg.reminders.chPush': 'Push',
    'schedcfg.reminders.chSms': 'SMS',
    'schedcfg.reminders.chEmail': 'Email',
    // OPEN-05 — the base statement is deliberately neutral about HOW reminders are obtained; the
    // remedy sentence below is appended ONLY when the server's acquisition_action says so.
    'schedcfg.reminders.unavailable': 'Appointment reminders are not included in your current plan.',
    'schedcfg.reminders.addOnRemedy': 'They are available as an add-on — see Billing & Subscription → Add-ons.',
    'schedcfg.reminders.smsUnavailable': 'SMS reminders are unavailable until SMS is configured and enabled for your business.',
    'schedcfg.general': 'General',
    'schedcfg.timezone': 'Timezone',
    'schedcfg.slotGranularity': 'Slot granularity (min)',
    'schedcfg.minNotice': 'Minimum notice (min)',
    'schedcfg.maxAdvance': 'Max advance (days)',
    'schedcfg.autoConfirm': 'Auto-confirm policy',
    'schedcfg.autoConfirmOff': 'Off',
    'schedcfg.autoConfirmRule': 'Rule-based',
    'schedcfg.mode': 'Scheduling mode',
    'schedcfg.industryPack': 'Industry pack ID',
    'schedcfg.workflowTemplate': 'Workflow template ID',
    'schedcfg.customerLabel': 'Customer display label',
    'schedcfg.resourceLabel': 'Resource display label',
    'schedcfg.hours': 'Business hours',
    'schedcfg.addWindow': 'Add window',
    'schedcfg.closed': 'Closed',
    'schedcfg.types': 'Appointment types',
    'schedcfg.types.inherited': 'These appointment types are set by the tenant’s Industry Profile and are not editable here.',
    'schedcfg.types.srcPack': 'Industry pack',
    'schedcfg.types.srcProfile': 'Industry profile',
    'schedcfg.types.srcTenant': 'Tenant configuration',
    'schedcfg.addType': 'Add type',
    'schedcfg.typeName': 'Name',
    'schedcfg.duration': 'Duration (min)',
    'schedcfg.preparation': 'Pre-visit preparation',
    'schedcfg.preparationPlaceholder': 'What should everyone booking this service do beforehand?',
    'schedcfg.preparationHint': 'Applies to every booking of this service — never write about an individual patient.',
    'schedcfg.buffer': 'Buffer (min)',
    'schedcfg.providers': 'Providers',
    'schedcfg.addProvider': 'Add provider',
    'schedcfg.providerName': 'Name',
    'schedcfg.staffId': 'Staff ID',
    'schedcfg.resourceType': 'Resource type',
    'schedcfg.resourceId': 'Resource ID',
    'schedcfg.providerTypes': 'Type IDs (comma-separated; empty = all)',
    'schedcfg.blackouts': 'Blackout windows',
    'schedcfg.addBlackout': 'Add blackout',
    'schedcfg.start': 'Start',
    'schedcfg.end': 'End',
    'schedcfg.reason': 'Reason',
    'schedcfg.providerOptional': 'Provider (optional)',
    'schedcfg.policies': 'Policies',
    'schedcfg.calendar.title': "Personal calendar",
    'schedcfg.calendar.intro': "Connect your own calendar so your outside commitments block your bookable times.",
    'schedcfg.calendar.loading': "Loading…",
    'schedcfg.calendar.statusLabel': "Status:",
    'schedcfg.calendar.statusConnected': "Connected",
    'schedcfg.calendar.statusAttention': "Needs attention",
    'schedcfg.calendar.statusNotConnected': "Not connected",
    'schedcfg.calendar.syncLabel': "Sync:",
    'schedcfg.calendar.syncOk': "up to date",
    'schedcfg.calendar.syncStale': "out of date",
    'schedcfg.calendar.syncError': "needs attention",
    'schedcfg.calendar.syncNever': "not synced yet",
    'schedcfg.calendar.lastSync': "Last sync {ts}",
    'schedcfg.calendar.attentionBanner': "Your calendar stopped syncing. Reconnect to keep your availability accurate.",
    'schedcfg.calendar.connect': "Connect {provider}",
    'schedcfg.calendar.reconnect': "Reconnect {provider}",
    'schedcfg.calendar.disconnect': "Disconnect {provider}",
    'schedcfg.calendar.unavailable': "{provider} — not available yet",
    'schedcfg.calendar.providerOther': "Other (CalDAV)",
    'schedcfg.feeApplies': 'Fee applies',
    'schedcfg.confirmationMode': 'Mode (manual / auto / rule_based)',
    'schedcfg.knowledgeRefsInput': 'Policy reference IDs (comma-separated)',
    'schedcfg.requiresDefaults': 'Default requirements',
    'schedcfg.remove': 'Remove',
    'schedcfg.mode.label': 'Mode',
    'schedcfg.mode.inPerson': 'In person',
    'schedcfg.mode.audio': 'Audio',
    'schedcfg.mode.video': 'Video',
    'sched.consult.label': 'Consultation',
    'sched.consult.joinAudio': 'Join audio',
    'sched.consult.joinVideo': 'Join video',
    'sched.consult.unavailable': 'Not available right now',
    'sched.consult.joining': 'Connecting…',
    'sched.consult.failed': 'Could not join. Please try again.',
    'schedcfg.doc.label': 'Required document',
    'schedcfg.doc.labelPlaceholder': 'e.g. Provincial government-issued ID',
    'schedcfg.doc.labelHelp': 'Name what the patient must provide. Left blank, the app can only say a document is required.',
    'schedcfg.doc.kindDocument': 'PDF or document',
    'schedcfg.doc.kindImage': 'Photo or scan',
    'schedcfg.doc.kindsEmpty': 'At least one kind must be allowed.',
    'schedcfg.day.monday': 'Monday',
    'schedcfg.day.tuesday': 'Tuesday',
    'schedcfg.day.wednesday': 'Wednesday',
    'schedcfg.day.thursday': 'Thursday',
    'schedcfg.day.friday': 'Friday',
    'schedcfg.day.saturday': 'Saturday',
    'schedcfg.day.sunday': 'Sunday',
  },

  es: {
    // ---- D5 appointment reminders (complete; OPEN-05 completed the block that shipped
    // English-only in 1ad4c4b). Terminology reused from the existing catalogue.
    'schedcfg.reminders.title': 'Recordatorios de citas',
    'schedcfg.reminders.enabled': 'Enviar recordatorios de citas',
    'schedcfg.reminders.offsets': 'Cuándo recordar (antes de la cita)',
    'schedcfg.reminders.o72': '72 horas antes',
    'schedcfg.reminders.o24': '24 horas antes',
    'schedcfg.reminders.o1': '1 hora antes',
    'schedcfg.reminders.channels': 'Canales de recordatorio',
    'schedcfg.reminders.chInApp': 'En la app',
    'schedcfg.reminders.chPush': 'Push',
    'schedcfg.reminders.chSms': 'SMS',
    'schedcfg.reminders.chEmail': 'Correo',
    'schedcfg.reminders.smsUnavailable': 'Los recordatorios por SMS no están disponibles hasta que se configure y active el SMS para tu negocio.',
    'schedcfg.reminders.unavailable': 'Los recordatorios de citas no están incluidos en tu plan actual.',
    'schedcfg.reminders.addOnRemedy': 'Están disponibles como complemento: consulta Facturación y suscripción → Complementos.',
    'sched.create.button': 'Nueva reserva',
    'sched.create.searchFailed': 'No se pudo buscar clientes. Inténtalo de nuevo.',
    'sched.create.title': 'Nueva cita',
    'sched.create.type': 'Tipo de cita',
    'sched.create.providerFilter': 'Proveedor (filtro opcional)',
    'sched.create.loadSlots': 'Cargar horarios disponibles',
    'sched.create.loadingSlots': 'Cargando horarios disponibles…',
    'sched.create.noSlots': 'No hay horarios disponibles — configura la disponibilidad primero.',
    'sched.create.noCrew': 'No hay personal disponible — asignar más tarde.',
    'sched.create.pickSlot': 'Elige un horario',
    'sched.create.customerId': 'ID de cliente',
    'sched.create.phone': 'Teléfono',
    'sched.create.email': 'Correo',
    'sched.create.customerHint': 'Indica al menos uno: ID de cliente, teléfono o correo. El servidor resuelve el cliente.',
    'sched.create.reason': 'Motivo (opcional)',
    'sched.create.submit': 'Crear reserva',
    'sched.create.needType': 'Selecciona un tipo de cita.',
    'sched.create.needCustomer': 'Indica un ID de cliente, teléfono o correo.',
    'sched.toast.created': 'Cita creada',
    'nav.schedulingSettings': 'Configuración de agenda',
    'navdesc.schedulingSettings': 'Horarios, tipos, proveedores, políticas',
    'page.schedConfig.title': 'Configuración de agenda',
    'page.schedConfig.subtitle': 'Configura disponibilidad, tipos, proveedores y políticas de {business}',
    'schedcfg.loading': 'Cargando configuración…',
    'schedcfg.failedLoad': 'Error al cargar la configuración',
    'schedcfg.none': 'Aún no hay configuración de agenda — configúrala abajo.',
    'schedcfg.save': 'Guardar configuración',
    'schedcfg.saved': 'Configuración guardada',
    'schedcfg.saveFailed': 'Error al guardar la configuración',
    'schedcfg.general': 'General',
    'schedcfg.timezone': 'Zona horaria',
    'schedcfg.slotGranularity': 'Granularidad de franjas (min)',
    'schedcfg.minNotice': 'Aviso mínimo (min)',
    'schedcfg.maxAdvance': 'Antelación máx. (días)',
    'schedcfg.autoConfirm': 'Política de autoconfirmación',
    'schedcfg.autoConfirmOff': 'Desactivada',
    'schedcfg.autoConfirmRule': 'Basada en reglas',
    'schedcfg.mode': 'Modo de agenda',
    'schedcfg.industryPack': 'ID de paquete de sector',
    'schedcfg.workflowTemplate': 'ID de plantilla de flujo',
    'schedcfg.customerLabel': 'Etiqueta de cliente',
    'schedcfg.resourceLabel': 'Etiqueta de recurso',
    'schedcfg.hours': 'Horario comercial',
    'schedcfg.addWindow': 'Añadir franja',
    'schedcfg.closed': 'Cerrado',
    'schedcfg.types': 'Tipos de cita',
    'schedcfg.types.inherited': 'These appointment types are set by the tenant’s Industry Profile and are not editable here.',
    'schedcfg.types.srcPack': 'Industry pack',
    'schedcfg.types.srcProfile': 'Industry profile',
    'schedcfg.types.srcTenant': 'Tenant configuration',
    'schedcfg.addType': 'Añadir tipo',
    'schedcfg.typeName': 'Nombre',
    'schedcfg.duration': 'Duración (min)',
    'schedcfg.preparation': 'Preparación previa a la visita',
    'schedcfg.preparationPlaceholder': '¿Qué debe hacer quien reserve este servicio antes de la cita?',
    'schedcfg.preparationHint': 'Se aplica a todas las reservas de este servicio: nunca escriba sobre un paciente concreto.',
    'schedcfg.buffer': 'Margen (min)',
    'schedcfg.providers': 'Proveedores',
    'schedcfg.addProvider': 'Añadir proveedor',
    'schedcfg.providerName': 'Nombre',
    'schedcfg.staffId': 'ID de personal',
    'schedcfg.resourceType': 'Tipo de recurso',
    'schedcfg.resourceId': 'ID de recurso',
    'schedcfg.providerTypes': 'IDs de tipo (separados por comas; vacío = todos)',
    'schedcfg.blackouts': 'Períodos bloqueados',
    'schedcfg.addBlackout': 'Añadir bloqueo',
    'schedcfg.start': 'Inicio',
    'schedcfg.end': 'Fin',
    'schedcfg.reason': 'Motivo',
    'schedcfg.providerOptional': 'Proveedor (opcional)',
    'schedcfg.policies': 'Políticas',
    'schedcfg.calendar.title': "Calendario personal",
    'schedcfg.calendar.intro': "Conecta tu propio calendario para que tus compromisos externos bloqueen tus horarios reservables.",
    'schedcfg.calendar.loading': "Cargando…",
    'schedcfg.calendar.statusLabel': "Estado:",
    'schedcfg.calendar.statusConnected': "Conectado",
    'schedcfg.calendar.statusAttention': "Requiere atención",
    'schedcfg.calendar.statusNotConnected': "No conectado",
    'schedcfg.calendar.syncLabel': "Sincronización:",
    'schedcfg.calendar.syncOk': "al día",
    'schedcfg.calendar.syncStale': "desactualizada",
    'schedcfg.calendar.syncError': "requiere atención",
    'schedcfg.calendar.syncNever': "aún sin sincronizar",
    'schedcfg.calendar.lastSync': "Última sincronización {ts}",
    'schedcfg.calendar.attentionBanner': "Tu calendario dejó de sincronizarse. Vuelve a conectarlo para mantener tu disponibilidad exacta.",
    'schedcfg.calendar.connect': "Conectar {provider}",
    'schedcfg.calendar.reconnect': "Volver a conectar {provider}",
    'schedcfg.calendar.disconnect': "Desconectar {provider}",
    'schedcfg.calendar.unavailable': "{provider}: aún no disponible",
    'schedcfg.calendar.providerOther': "Otro (CalDAV)",
    'schedcfg.feeApplies': 'Se aplica tarifa',
    'schedcfg.confirmationMode': 'Modo (manual / auto / rule_based)',
    'schedcfg.knowledgeRefsInput': 'IDs de referencia de política (separados por comas)',
    'schedcfg.requiresDefaults': 'Requisitos por defecto',
    'schedcfg.remove': 'Quitar',
    'schedcfg.mode.label': 'Mode',
    'schedcfg.mode.inPerson': 'In person',
    'schedcfg.mode.audio': 'Audio',
    'schedcfg.mode.video': 'Video',
    'sched.consult.label': 'Consulta',
    'sched.consult.joinAudio': 'Unirse por audio',
    'sched.consult.joinVideo': 'Unirse por video',
    'sched.consult.unavailable': 'No disponible en este momento',
    'sched.consult.joining': 'Conectando…',
    'sched.consult.failed': 'No se pudo unir. Inténtalo de nuevo.',
    'schedcfg.doc.label': 'Required document',
    'schedcfg.doc.labelPlaceholder': 'e.g. Provincial government-issued ID',
    'schedcfg.doc.labelHelp': 'Name what the patient must provide. Left blank, the app can only say a document is required.',
    'schedcfg.doc.kindDocument': 'PDF or document',
    'schedcfg.doc.kindImage': 'Photo or scan',
    'schedcfg.doc.kindsEmpty': 'At least one kind must be allowed.',
    'schedcfg.day.monday': 'Lunes',
    'schedcfg.day.tuesday': 'Martes',
    'schedcfg.day.wednesday': 'Miércoles',
    'schedcfg.day.thursday': 'Jueves',
    'schedcfg.day.friday': 'Viernes',
    'schedcfg.day.saturday': 'Sábado',
    'schedcfg.day.sunday': 'Domingo',
  },

  fr: {
    // ---- D5 appointment reminders (complete; OPEN-05 completed the block that shipped
    // English-only in 1ad4c4b). Terminology reused from the existing catalogue.
    'schedcfg.reminders.title': 'Rappels de rendez-vous',
    'schedcfg.reminders.enabled': 'Envoyer des rappels de rendez-vous',
    'schedcfg.reminders.offsets': 'Quand rappeler (avant le rendez-vous)',
    'schedcfg.reminders.o72': '72 heures avant',
    'schedcfg.reminders.o24': '24 heures avant',
    'schedcfg.reminders.o1': '1 heure avant',
    'schedcfg.reminders.channels': 'Canaux de rappel',
    'schedcfg.reminders.chInApp': 'Dans l’app',
    'schedcfg.reminders.chPush': 'Push',
    'schedcfg.reminders.chSms': 'SMS',
    'schedcfg.reminders.chEmail': 'E-mail',
    'schedcfg.reminders.smsUnavailable': 'Les rappels par SMS sont indisponibles tant que le SMS n’est pas configuré et activé pour votre entreprise.',
    'schedcfg.reminders.unavailable': 'Les rappels de rendez-vous ne sont pas inclus dans votre forfait actuel.',
    'schedcfg.reminders.addOnRemedy': 'Ils sont disponibles en option : voir Facturation et abonnement → Options.',
    'sched.create.button': 'Nouvelle réservation',
    'sched.create.searchFailed': 'Impossible de rechercher les clients. Réessayez.',
    'sched.create.title': 'Nouveau rendez-vous',
    'sched.create.type': 'Type de rendez-vous',
    'sched.create.providerFilter': 'Prestataire (filtre facultatif)',
    'sched.create.loadSlots': 'Charger les horaires disponibles',
    'sched.create.loadingSlots': 'Chargement des horaires disponibles…',
    'sched.create.noSlots': 'Aucun horaire disponible — configurez d’abord la disponibilité.',
    'sched.create.noCrew': 'Aucun personnel disponible — à affecter plus tard.',
    'sched.create.pickSlot': 'Choisissez un horaire',
    'sched.create.customerId': 'ID client',
    'sched.create.phone': 'Téléphone',
    'sched.create.email': 'E-mail',
    'sched.create.customerHint': 'Indiquez au moins un : ID client, téléphone ou e-mail. Le serveur résout le client.',
    'sched.create.reason': 'Motif (facultatif)',
    'sched.create.submit': 'Créer la réservation',
    'sched.create.needType': 'Sélectionnez un type de rendez-vous.',
    'sched.create.needCustomer': 'Indiquez un ID client, un téléphone ou un e-mail.',
    'sched.toast.created': 'Rendez-vous créé',
    'nav.schedulingSettings': 'Paramètres de planning',
    'navdesc.schedulingSettings': 'Horaires, types, prestataires, politiques',
    'page.schedConfig.title': 'Paramètres de planning',
    'page.schedConfig.subtitle': 'Configurer la disponibilité, les types, les prestataires et les politiques de {business}',
    'schedcfg.loading': 'Chargement de la configuration…',
    'schedcfg.failedLoad': 'Échec du chargement de la configuration',
    'schedcfg.none': 'Aucune configuration de planning — configurez-la ci-dessous.',
    'schedcfg.save': 'Enregistrer la configuration',
    'schedcfg.saved': 'Configuration enregistrée',
    'schedcfg.saveFailed': 'Échec de l’enregistrement de la configuration',
    'schedcfg.general': 'Général',
    'schedcfg.timezone': 'Fuseau horaire',
    'schedcfg.slotGranularity': 'Granularité des créneaux (min)',
    'schedcfg.minNotice': 'Préavis minimum (min)',
    'schedcfg.maxAdvance': 'Anticipation max. (jours)',
    'schedcfg.autoConfirm': 'Politique d’auto-confirmation',
    'schedcfg.autoConfirmOff': 'Désactivée',
    'schedcfg.autoConfirmRule': 'Basée sur des règles',
    'schedcfg.mode': 'Mode de planification',
    'schedcfg.industryPack': 'ID de pack sectoriel',
    'schedcfg.workflowTemplate': 'ID de modèle de flux',
    'schedcfg.customerLabel': 'Libellé client',
    'schedcfg.resourceLabel': 'Libellé ressource',
    'schedcfg.hours': 'Heures d’ouverture',
    'schedcfg.addWindow': 'Ajouter une plage',
    'schedcfg.closed': 'Fermé',
    'schedcfg.types': 'Types de rendez-vous',
    'schedcfg.types.inherited': 'These appointment types are set by the tenant’s Industry Profile and are not editable here.',
    'schedcfg.types.srcPack': 'Industry pack',
    'schedcfg.types.srcProfile': 'Industry profile',
    'schedcfg.types.srcTenant': 'Tenant configuration',
    'schedcfg.addType': 'Ajouter un type',
    'schedcfg.typeName': 'Nom',
    'schedcfg.duration': 'Durée (min)',
    'schedcfg.preparation': 'Préparation avant la visite',
    'schedcfg.preparationPlaceholder': 'Que doit faire toute personne qui réserve ce service au préalable ?',
    'schedcfg.preparationHint': 'S’applique à toutes les réservations de ce service — n’écrivez jamais au sujet d’un patient en particulier.',
    'schedcfg.buffer': 'Marge (min)',
    'schedcfg.providers': 'Prestataires',
    'schedcfg.addProvider': 'Ajouter un prestataire',
    'schedcfg.providerName': 'Nom',
    'schedcfg.staffId': 'ID du personnel',
    'schedcfg.resourceType': 'Type de ressource',
    'schedcfg.resourceId': 'ID de ressource',
    'schedcfg.providerTypes': 'IDs de type (séparés par des virgules ; vide = tous)',
    'schedcfg.blackouts': 'Périodes bloquées',
    'schedcfg.addBlackout': 'Ajouter un blocage',
    'schedcfg.start': 'Début',
    'schedcfg.end': 'Fin',
    'schedcfg.reason': 'Motif',
    'schedcfg.providerOptional': 'Prestataire (facultatif)',
    'schedcfg.policies': 'Politiques',
    'schedcfg.calendar.title': "Agenda personnelle",
    'schedcfg.calendar.intro': "Connectez votre agenda pour que vos engagements externes bloquent vos créneaux réservables.",
    'schedcfg.calendar.loading': "Chargement…",
    'schedcfg.calendar.statusLabel': "Statut :",
    'schedcfg.calendar.statusConnected': "Connecté",
    'schedcfg.calendar.statusAttention': "Attention requise",
    'schedcfg.calendar.statusNotConnected': "Non connecté",
    'schedcfg.calendar.syncLabel': "Synchronisation :",
    'schedcfg.calendar.syncOk': "à jour",
    'schedcfg.calendar.syncStale': "obsolète",
    'schedcfg.calendar.syncError': "attention requise",
    'schedcfg.calendar.syncNever': "pas encore synchronisé",
    'schedcfg.calendar.lastSync': "Dernière synchronisation {ts}",
    'schedcfg.calendar.attentionBanner': "Votre agenda ne se synchronise plus. Reconnectez-le pour que vos disponibilités restent exactes.",
    'schedcfg.calendar.connect': "Connecter {provider}",
    'schedcfg.calendar.reconnect': "Reconnecter {provider}",
    'schedcfg.calendar.disconnect': "Déconnecter {provider}",
    'schedcfg.calendar.unavailable': "{provider} — pas encore disponible",
    'schedcfg.calendar.providerOther': "Autre (CalDAV)",
    'schedcfg.feeApplies': 'Des frais s’appliquent',
    'schedcfg.confirmationMode': 'Mode (manual / auto / rule_based)',
    'schedcfg.knowledgeRefsInput': 'IDs de référence de politique (séparés par des virgules)',
    'schedcfg.requiresDefaults': 'Exigences par défaut',
    'schedcfg.remove': 'Retirer',
    'schedcfg.mode.label': 'Mode',
    'schedcfg.mode.inPerson': 'In person',
    'schedcfg.mode.audio': 'Audio',
    'schedcfg.mode.video': 'Video',
    'sched.consult.label': 'Consultation',
    'sched.consult.joinAudio': 'Rejoindre en audio',
    'sched.consult.joinVideo': 'Rejoindre en vidéo',
    'sched.consult.unavailable': 'Indisponible pour le moment',
    'sched.consult.joining': 'Connexion…',
    'sched.consult.failed': 'Impossible de rejoindre. Veuillez réessayer.',
    'schedcfg.doc.label': 'Required document',
    'schedcfg.doc.labelPlaceholder': 'e.g. Provincial government-issued ID',
    'schedcfg.doc.labelHelp': 'Name what the patient must provide. Left blank, the app can only say a document is required.',
    'schedcfg.doc.kindDocument': 'PDF or document',
    'schedcfg.doc.kindImage': 'Photo or scan',
    'schedcfg.doc.kindsEmpty': 'At least one kind must be allowed.',
    'schedcfg.day.monday': 'Lundi',
    'schedcfg.day.tuesday': 'Mardi',
    'schedcfg.day.wednesday': 'Mercredi',
    'schedcfg.day.thursday': 'Jeudi',
    'schedcfg.day.friday': 'Vendredi',
    'schedcfg.day.saturday': 'Samedi',
    'schedcfg.day.sunday': 'Dimanche',
  },

  pt: {
    // ---- D5 appointment reminders (complete; OPEN-05 completed the block that shipped
    // English-only in 1ad4c4b). Terminology reused from the existing catalogue.
    'schedcfg.reminders.title': 'Lembretes de compromissos',
    'schedcfg.reminders.enabled': 'Enviar lembretes de compromissos',
    'schedcfg.reminders.offsets': 'Quando lembrar (antes do compromisso)',
    'schedcfg.reminders.o72': '72 horas antes',
    'schedcfg.reminders.o24': '24 horas antes',
    'schedcfg.reminders.o1': '1 hora antes',
    'schedcfg.reminders.channels': 'Canais de lembrete',
    'schedcfg.reminders.chInApp': 'No app',
    'schedcfg.reminders.chPush': 'Push',
    'schedcfg.reminders.chSms': 'SMS',
    'schedcfg.reminders.chEmail': 'E-mail',
    'schedcfg.reminders.smsUnavailable': 'Os lembretes por SMS ficam indisponíveis até que o SMS seja configurado e ativado para a sua empresa.',
    'schedcfg.reminders.unavailable': 'Os lembretes de compromissos não estão incluídos no seu plano atual.',
    'schedcfg.reminders.addOnRemedy': 'Estão disponíveis como complemento: consulte Faturamento e assinatura → Complementos.',
    'sched.create.button': 'Nova reserva',
    'sched.create.searchFailed': 'Não foi possível pesquisar clientes. Tente novamente.',
    'sched.create.title': 'Nova consulta',
    'sched.create.type': 'Tipo de consulta',
    'sched.create.providerFilter': 'Prestador (filtro opcional)',
    'sched.create.loadSlots': 'Carregar horários disponíveis',
    'sched.create.loadingSlots': 'A carregar horários disponíveis…',
    'sched.create.noSlots': 'Sem horários disponíveis — configure a disponibilidade primeiro.',
    'sched.create.noCrew': 'Nenhum funcionário disponível — atribuir depois.',
    'sched.create.pickSlot': 'Escolha um horário',
    'sched.create.customerId': 'ID do cliente',
    'sched.create.phone': 'Telefone',
    'sched.create.email': 'E-mail',
    'sched.create.customerHint': 'Indique pelo menos um: ID do cliente, telefone ou e-mail. O servidor resolve o cliente.',
    'sched.create.reason': 'Motivo (opcional)',
    'sched.create.submit': 'Criar reserva',
    'sched.create.needType': 'Selecione um tipo de consulta.',
    'sched.create.needCustomer': 'Indique um ID de cliente, telefone ou e-mail.',
    'sched.toast.created': 'Consulta criada',
    'nav.schedulingSettings': 'Definições de agenda',
    'navdesc.schedulingSettings': 'Horários, tipos, prestadores, políticas',
    'page.schedConfig.title': 'Definições de agenda',
    'page.schedConfig.subtitle': 'Configure disponibilidade, tipos, prestadores e políticas de {business}',
    'schedcfg.loading': 'A carregar configuração…',
    'schedcfg.failedLoad': 'Falha ao carregar a configuração',
    'schedcfg.none': 'Ainda sem configuração de agenda — configure abaixo.',
    'schedcfg.save': 'Guardar configuração',
    'schedcfg.saved': 'Configuração guardada',
    'schedcfg.saveFailed': 'Falha ao guardar a configuração',
    'schedcfg.general': 'Geral',
    'schedcfg.timezone': 'Fuso horário',
    'schedcfg.slotGranularity': 'Granularidade de intervalos (min)',
    'schedcfg.minNotice': 'Aviso mínimo (min)',
    'schedcfg.maxAdvance': 'Antecedência máx. (dias)',
    'schedcfg.autoConfirm': 'Política de autoconfirmação',
    'schedcfg.autoConfirmOff': 'Desligada',
    'schedcfg.autoConfirmRule': 'Baseada em regras',
    'schedcfg.mode': 'Modo de agenda',
    'schedcfg.industryPack': 'ID do pacote de setor',
    'schedcfg.workflowTemplate': 'ID de modelo de fluxo',
    'schedcfg.customerLabel': 'Rótulo de cliente',
    'schedcfg.resourceLabel': 'Rótulo de recurso',
    'schedcfg.hours': 'Horário comercial',
    'schedcfg.addWindow': 'Adicionar intervalo',
    'schedcfg.closed': 'Fechado',
    'schedcfg.types': 'Tipos de consulta',
    'schedcfg.types.inherited': 'These appointment types are set by the tenant’s Industry Profile and are not editable here.',
    'schedcfg.types.srcPack': 'Industry pack',
    'schedcfg.types.srcProfile': 'Industry profile',
    'schedcfg.types.srcTenant': 'Tenant configuration',
    'schedcfg.addType': 'Adicionar tipo',
    'schedcfg.typeName': 'Nome',
    'schedcfg.duration': 'Duração (min)',
    'schedcfg.preparation': 'Preparação pré-consulta',
    'schedcfg.preparationPlaceholder': 'O que quem agenda este serviço deve fazer antes?',
    'schedcfg.preparationHint': 'Aplica-se a todas as reservas deste serviço — nunca escreva sobre um paciente específico.',
    'schedcfg.buffer': 'Margem (min)',
    'schedcfg.providers': 'Prestadores',
    'schedcfg.addProvider': 'Adicionar prestador',
    'schedcfg.providerName': 'Nome',
    'schedcfg.staffId': 'ID do funcionário',
    'schedcfg.resourceType': 'Tipo de recurso',
    'schedcfg.resourceId': 'ID de recurso',
    'schedcfg.providerTypes': 'IDs de tipo (separados por vírgulas; vazio = todos)',
    'schedcfg.blackouts': 'Períodos bloqueados',
    'schedcfg.addBlackout': 'Adicionar bloqueio',
    'schedcfg.start': 'Início',
    'schedcfg.end': 'Fim',
    'schedcfg.reason': 'Motivo',
    'schedcfg.providerOptional': 'Prestador (opcional)',
    'schedcfg.policies': 'Políticas',
    'schedcfg.calendar.title': "Calendário pessoal",
    'schedcfg.calendar.intro': "Conecte o seu calendário para que os seus compromissos externos bloqueiem os horários reserváveis.",
    'schedcfg.calendar.loading': "Carregando…",
    'schedcfg.calendar.statusLabel': "Estado:",
    'schedcfg.calendar.statusConnected': "Conectado",
    'schedcfg.calendar.statusAttention': "Requer atenção",
    'schedcfg.calendar.statusNotConnected': "Não conectado",
    'schedcfg.calendar.syncLabel': "Sincronização:",
    'schedcfg.calendar.syncOk': "em dia",
    'schedcfg.calendar.syncStale': "desatualizada",
    'schedcfg.calendar.syncError': "requer atenção",
    'schedcfg.calendar.syncNever': "ainda não sincronizado",
    'schedcfg.calendar.lastSync': "Última sincronização {ts}",
    'schedcfg.calendar.attentionBanner': "O seu calendário parou de sincronizar. Reconecte-o para manter a sua disponibilidade correta.",
    'schedcfg.calendar.connect': "Conectar {provider}",
    'schedcfg.calendar.reconnect': "Reconectar {provider}",
    'schedcfg.calendar.disconnect': "Desconectar {provider}",
    'schedcfg.calendar.unavailable': "{provider} — ainda não disponível",
    'schedcfg.calendar.providerOther': "Outro (CalDAV)",
    'schedcfg.feeApplies': 'Aplica-se taxa',
    'schedcfg.confirmationMode': 'Modo (manual / auto / rule_based)',
    'schedcfg.knowledgeRefsInput': 'IDs de referência de política (separados por vírgulas)',
    'schedcfg.requiresDefaults': 'Requisitos padrão',
    'schedcfg.remove': 'Remover',
    'schedcfg.mode.label': 'Mode',
    'schedcfg.mode.inPerson': 'In person',
    'schedcfg.mode.audio': 'Audio',
    'schedcfg.mode.video': 'Video',
    'sched.consult.label': 'Consulta',
    'sched.consult.joinAudio': 'Entrar por áudio',
    'sched.consult.joinVideo': 'Entrar por vídeo',
    'sched.consult.unavailable': 'Indisponível no momento',
    'sched.consult.joining': 'Conectando…',
    'sched.consult.failed': 'Não foi possível entrar. Tente novamente.',
    'schedcfg.doc.label': 'Required document',
    'schedcfg.doc.labelPlaceholder': 'e.g. Provincial government-issued ID',
    'schedcfg.doc.labelHelp': 'Name what the patient must provide. Left blank, the app can only say a document is required.',
    'schedcfg.doc.kindDocument': 'PDF or document',
    'schedcfg.doc.kindImage': 'Photo or scan',
    'schedcfg.doc.kindsEmpty': 'At least one kind must be allowed.',
    'schedcfg.day.monday': 'Segunda',
    'schedcfg.day.tuesday': 'Terça',
    'schedcfg.day.wednesday': 'Quarta',
    'schedcfg.day.thursday': 'Quinta',
    'schedcfg.day.friday': 'Sexta',
    'schedcfg.day.saturday': 'Sábado',
    'schedcfg.day.sunday': 'Domingo',
  },

  de: {
    // ---- D5 appointment reminders (complete; OPEN-05 completed the block that shipped
    // English-only in 1ad4c4b). Terminology reused from the existing catalogue.
    'schedcfg.reminders.title': 'Terminerinnerungen',
    'schedcfg.reminders.enabled': 'Terminerinnerungen senden',
    'schedcfg.reminders.offsets': 'Wann erinnern (vor dem Termin)',
    'schedcfg.reminders.o72': '72 Stunden vorher',
    'schedcfg.reminders.o24': '24 Stunden vorher',
    'schedcfg.reminders.o1': '1 Stunde vorher',
    'schedcfg.reminders.channels': 'Erinnerungskanäle',
    'schedcfg.reminders.chInApp': 'In-App',
    'schedcfg.reminders.chPush': 'Push',
    'schedcfg.reminders.chSms': 'SMS',
    'schedcfg.reminders.chEmail': 'E-Mail',
    'schedcfg.reminders.smsUnavailable': 'SMS-Erinnerungen sind nicht verfügbar, solange SMS für Ihr Unternehmen nicht konfiguriert und aktiviert ist.',
    'schedcfg.reminders.unavailable': 'Terminerinnerungen sind in Ihrem aktuellen Tarif nicht enthalten.',
    'schedcfg.reminders.addOnRemedy': 'Sie sind als Zusatzmodul verfügbar – siehe Abrechnung & Abonnement → Zusatzmodule.',
    'sched.create.button': 'Neue Buchung',
    'sched.create.searchFailed': 'Kundensuche nicht möglich. Bitte erneut versuchen.',
    'sched.create.title': 'Neuer Termin',
    'sched.create.type': 'Terminart',
    'sched.create.providerFilter': 'Anbieter (optionaler Filter)',
    'sched.create.loadSlots': 'Verfügbare Zeiten laden',
    'sched.create.loadingSlots': 'Verfügbare Zeiten werden geladen…',
    'sched.create.noSlots': 'Keine verfügbaren Zeiten — konfigurieren Sie zuerst die Verfügbarkeit.',
    'sched.create.noCrew': 'Kein passendes Personal — später zuweisen.',
    'sched.create.pickSlot': 'Zeit wählen',
    'sched.create.customerId': 'Kunden-ID',
    'sched.create.phone': 'Telefon',
    'sched.create.email': 'E-Mail',
    'sched.create.customerHint': 'Geben Sie mindestens eines an: Kunden-ID, Telefon oder E-Mail. Der Server ermittelt den Kunden.',
    'sched.create.reason': 'Grund (optional)',
    'sched.create.submit': 'Buchung erstellen',
    'sched.create.needType': 'Wählen Sie eine Terminart.',
    'sched.create.needCustomer': 'Geben Sie eine Kunden-ID, Telefon oder E-Mail an.',
    'sched.toast.created': 'Termin erstellt',
    'nav.schedulingSettings': 'Termin-Einstellungen',
    'navdesc.schedulingSettings': 'Zeiten, Arten, Anbieter, Richtlinien',
    'page.schedConfig.title': 'Termin-Einstellungen',
    'page.schedConfig.subtitle': 'Verfügbarkeit, Arten, Anbieter und Richtlinien für {business} konfigurieren',
    'schedcfg.loading': 'Konfiguration wird geladen…',
    'schedcfg.failedLoad': 'Konfiguration konnte nicht geladen werden',
    'schedcfg.none': 'Noch keine Terminkonfiguration — richten Sie sie unten ein.',
    'schedcfg.save': 'Konfiguration speichern',
    'schedcfg.saved': 'Konfiguration gespeichert',
    'schedcfg.saveFailed': 'Konfiguration konnte nicht gespeichert werden',
    'schedcfg.general': 'Allgemein',
    'schedcfg.timezone': 'Zeitzone',
    'schedcfg.slotGranularity': 'Slot-Granularität (Min)',
    'schedcfg.minNotice': 'Mindestvorlauf (Min)',
    'schedcfg.maxAdvance': 'Max. Vorausbuchung (Tage)',
    'schedcfg.autoConfirm': 'Auto-Bestätigungsrichtlinie',
    'schedcfg.autoConfirmOff': 'Aus',
    'schedcfg.autoConfirmRule': 'Regelbasiert',
    'schedcfg.mode': 'Planungsmodus',
    'schedcfg.industryPack': 'Branchenpaket-ID',
    'schedcfg.workflowTemplate': 'Workflow-Vorlagen-ID',
    'schedcfg.customerLabel': 'Kundenbezeichnung',
    'schedcfg.resourceLabel': 'Ressourcenbezeichnung',
    'schedcfg.hours': 'Geschäftszeiten',
    'schedcfg.addWindow': 'Zeitfenster hinzufügen',
    'schedcfg.closed': 'Geschlossen',
    'schedcfg.types': 'Terminarten',
    'schedcfg.types.inherited': 'These appointment types are set by the tenant’s Industry Profile and are not editable here.',
    'schedcfg.types.srcPack': 'Industry pack',
    'schedcfg.types.srcProfile': 'Industry profile',
    'schedcfg.types.srcTenant': 'Tenant configuration',
    'schedcfg.addType': 'Art hinzufügen',
    'schedcfg.typeName': 'Name',
    'schedcfg.duration': 'Dauer (Min)',
    'schedcfg.preparation': 'Vorbereitung vor dem Termin',
    'schedcfg.preparationPlaceholder': 'Was sollten alle, die diesen Service buchen, vorher tun?',
    'schedcfg.preparationHint': 'Gilt für jede Buchung dieses Services — schreiben Sie nie über einzelne Patienten.',
    'schedcfg.buffer': 'Puffer (Min)',
    'schedcfg.providers': 'Anbieter',
    'schedcfg.addProvider': 'Anbieter hinzufügen',
    'schedcfg.providerName': 'Name',
    'schedcfg.staffId': 'Mitarbeiter-ID',
    'schedcfg.resourceType': 'Ressourcentyp',
    'schedcfg.resourceId': 'Ressourcen-ID',
    'schedcfg.providerTypes': 'Typ-IDs (kommagetrennt; leer = alle)',
    'schedcfg.blackouts': 'Sperrzeiten',
    'schedcfg.addBlackout': 'Sperrzeit hinzufügen',
    'schedcfg.start': 'Beginn',
    'schedcfg.end': 'Ende',
    'schedcfg.reason': 'Grund',
    'schedcfg.providerOptional': 'Anbieter (optional)',
    'schedcfg.policies': 'Richtlinien',
    'schedcfg.calendar.title': "Persönlicher Kalender",
    'schedcfg.calendar.intro': "Verbinden Sie Ihren Kalender, damit externe Termine Ihre buchbaren Zeiten blockieren.",
    'schedcfg.calendar.loading': "Wird geladen…",
    'schedcfg.calendar.statusLabel': "Status:",
    'schedcfg.calendar.statusConnected': "Verbunden",
    'schedcfg.calendar.statusAttention': "Aktion erforderlich",
    'schedcfg.calendar.statusNotConnected': "Nicht verbunden",
    'schedcfg.calendar.syncLabel': "Synchronisierung:",
    'schedcfg.calendar.syncOk': "aktuell",
    'schedcfg.calendar.syncStale': "veraltet",
    'schedcfg.calendar.syncError': "Aktion erforderlich",
    'schedcfg.calendar.syncNever': "noch nicht synchronisiert",
    'schedcfg.calendar.lastSync': "Letzte Synchronisierung {ts}",
    'schedcfg.calendar.attentionBanner': "Ihr Kalender wird nicht mehr synchronisiert. Verbinden Sie ihn neu, damit Ihre Verfügbarkeit korrekt bleibt.",
    'schedcfg.calendar.connect': "{provider} verbinden",
    'schedcfg.calendar.reconnect': "{provider} neu verbinden",
    'schedcfg.calendar.disconnect': "{provider} trennen",
    'schedcfg.calendar.unavailable': "{provider} — noch nicht verfügbar",
    'schedcfg.calendar.providerOther': "Andere (CalDAV)",
    'schedcfg.feeApplies': 'Gebühr fällt an',
    'schedcfg.confirmationMode': 'Modus (manual / auto / rule_based)',
    'schedcfg.knowledgeRefsInput': 'Richtlinien-Referenz-IDs (kommagetrennt)',
    'schedcfg.requiresDefaults': 'Standardanforderungen',
    'schedcfg.remove': 'Entfernen',
    'schedcfg.mode.label': 'Mode',
    'schedcfg.mode.inPerson': 'In person',
    'schedcfg.mode.audio': 'Audio',
    'schedcfg.mode.video': 'Video',
    'sched.consult.label': 'Beratung',
    'sched.consult.joinAudio': 'Per Audio beitreten',
    'sched.consult.joinVideo': 'Per Video beitreten',
    'sched.consult.unavailable': 'Derzeit nicht verfügbar',
    'sched.consult.joining': 'Verbindung wird hergestellt…',
    'sched.consult.failed': 'Beitritt nicht möglich. Bitte erneut versuchen.',
    'schedcfg.doc.label': 'Required document',
    'schedcfg.doc.labelPlaceholder': 'e.g. Provincial government-issued ID',
    'schedcfg.doc.labelHelp': 'Name what the patient must provide. Left blank, the app can only say a document is required.',
    'schedcfg.doc.kindDocument': 'PDF or document',
    'schedcfg.doc.kindImage': 'Photo or scan',
    'schedcfg.doc.kindsEmpty': 'At least one kind must be allowed.',
    'schedcfg.day.monday': 'Montag',
    'schedcfg.day.tuesday': 'Dienstag',
    'schedcfg.day.wednesday': 'Mittwoch',
    'schedcfg.day.thursday': 'Donnerstag',
    'schedcfg.day.friday': 'Freitag',
    'schedcfg.day.saturday': 'Samstag',
    'schedcfg.day.sunday': 'Sonntag',
  },

  it: {
    // ---- D5 appointment reminders (complete; OPEN-05 completed the block that shipped
    // English-only in 1ad4c4b). Terminology reused from the existing catalogue.
    'schedcfg.reminders.title': 'Promemoria appuntamenti',
    'schedcfg.reminders.enabled': 'Invia promemoria appuntamenti',
    'schedcfg.reminders.offsets': 'Quando ricordare (prima dell’appuntamento)',
    'schedcfg.reminders.o72': '72 ore prima',
    'schedcfg.reminders.o24': '24 ore prima',
    'schedcfg.reminders.o1': '1 ora prima',
    'schedcfg.reminders.channels': 'Canali dei promemoria',
    'schedcfg.reminders.chInApp': 'In-app',
    'schedcfg.reminders.chPush': 'Push',
    'schedcfg.reminders.chSms': 'SMS',
    'schedcfg.reminders.chEmail': 'Email',
    'schedcfg.reminders.smsUnavailable': 'I promemoria via SMS non sono disponibili finché l’SMS non è configurato e attivo per la tua azienda.',
    'schedcfg.reminders.unavailable': 'I promemoria degli appuntamenti non sono inclusi nel tuo piano attuale.',
    'schedcfg.reminders.addOnRemedy': 'Sono disponibili come componente aggiuntivo: vedi Fatturazione e abbonamento → Componenti aggiuntivi.',
    'sched.create.button': 'Nuova prenotazione',
    'sched.create.searchFailed': 'Impossibile cercare i clienti. Riprova.',
    'sched.create.title': 'Nuovo appuntamento',
    'sched.create.type': 'Tipo di appuntamento',
    'sched.create.providerFilter': 'Fornitore (filtro facoltativo)',
    'sched.create.loadSlots': 'Carica orari disponibili',
    'sched.create.loadingSlots': 'Caricamento orari disponibili…',
    'sched.create.noSlots': 'Nessun orario disponibile — configura prima la disponibilità.',
    'sched.create.noCrew': 'Nessun personale disponibile — assegna più tardi.',
    'sched.create.pickSlot': 'Scegli un orario',
    'sched.create.customerId': 'ID cliente',
    'sched.create.phone': 'Telefono',
    'sched.create.email': 'E-mail',
    'sched.create.customerHint': 'Indica almeno uno: ID cliente, telefono o e-mail. Il server risolve il cliente.',
    'sched.create.reason': 'Motivo (facoltativo)',
    'sched.create.submit': 'Crea prenotazione',
    'sched.create.needType': 'Seleziona un tipo di appuntamento.',
    'sched.create.needCustomer': 'Indica un ID cliente, telefono o e-mail.',
    'sched.toast.created': 'Appuntamento creato',
    'nav.schedulingSettings': 'Impostazioni agenda',
    'navdesc.schedulingSettings': 'Orari, tipi, fornitori, politiche',
    'page.schedConfig.title': 'Impostazioni agenda',
    'page.schedConfig.subtitle': 'Configura disponibilità, tipi, fornitori e politiche di {business}',
    'schedcfg.loading': 'Caricamento configurazione…',
    'schedcfg.failedLoad': 'Impossibile caricare la configurazione',
    'schedcfg.none': 'Nessuna configurazione agenda — impostala qui sotto.',
    'schedcfg.save': 'Salva configurazione',
    'schedcfg.saved': 'Configurazione salvata',
    'schedcfg.saveFailed': 'Impossibile salvare la configurazione',
    'schedcfg.general': 'Generale',
    'schedcfg.timezone': 'Fuso orario',
    'schedcfg.slotGranularity': 'Granularità fasce (min)',
    'schedcfg.minNotice': 'Preavviso minimo (min)',
    'schedcfg.maxAdvance': 'Anticipo max (giorni)',
    'schedcfg.autoConfirm': 'Politica di autoconferma',
    'schedcfg.autoConfirmOff': 'Disattivata',
    'schedcfg.autoConfirmRule': 'Basata su regole',
    'schedcfg.mode': 'Modalità agenda',
    'schedcfg.industryPack': 'ID pacchetto settore',
    'schedcfg.workflowTemplate': 'ID modello flusso',
    'schedcfg.customerLabel': 'Etichetta cliente',
    'schedcfg.resourceLabel': 'Etichetta risorsa',
    'schedcfg.hours': 'Orari di apertura',
    'schedcfg.addWindow': 'Aggiungi fascia',
    'schedcfg.closed': 'Chiuso',
    'schedcfg.types': 'Tipi di appuntamento',
    'schedcfg.types.inherited': 'These appointment types are set by the tenant’s Industry Profile and are not editable here.',
    'schedcfg.types.srcPack': 'Industry pack',
    'schedcfg.types.srcProfile': 'Industry profile',
    'schedcfg.types.srcTenant': 'Tenant configuration',
    'schedcfg.addType': 'Aggiungi tipo',
    'schedcfg.typeName': 'Nome',
    'schedcfg.duration': 'Durata (min)',
    'schedcfg.preparation': 'Preparazione prima della visita',
    'schedcfg.preparationPlaceholder': 'Cosa deve fare chi prenota questo servizio?',
    'schedcfg.preparationHint': 'Vale per tutte le prenotazioni di questo servizio: non scrivere mai di un singolo paziente.',
    'schedcfg.buffer': 'Margine (min)',
    'schedcfg.providers': 'Fornitori',
    'schedcfg.addProvider': 'Aggiungi fornitore',
    'schedcfg.providerName': 'Nome',
    'schedcfg.staffId': 'ID personale',
    'schedcfg.resourceType': 'Tipo di risorsa',
    'schedcfg.resourceId': 'ID risorsa',
    'schedcfg.providerTypes': 'ID tipo (separati da virgole; vuoto = tutti)',
    'schedcfg.blackouts': 'Periodi di blocco',
    'schedcfg.addBlackout': 'Aggiungi blocco',
    'schedcfg.start': 'Inizio',
    'schedcfg.end': 'Fine',
    'schedcfg.reason': 'Motivo',
    'schedcfg.providerOptional': 'Fornitore (facoltativo)',
    'schedcfg.policies': 'Politiche',
    'schedcfg.calendar.title': "Calendario personale",
    'schedcfg.calendar.intro': "Collega il tuo calendario così gli impegni esterni bloccano i tuoi orari prenotabili.",
    'schedcfg.calendar.loading': "Caricamento…",
    'schedcfg.calendar.statusLabel': "Stato:",
    'schedcfg.calendar.statusConnected': "Connesso",
    'schedcfg.calendar.statusAttention': "Richiede attenzione",
    'schedcfg.calendar.statusNotConnected': "Non connesso",
    'schedcfg.calendar.syncLabel': "Sincronizzazione:",
    'schedcfg.calendar.syncOk': "aggiornata",
    'schedcfg.calendar.syncStale': "non aggiornata",
    'schedcfg.calendar.syncError': "richiede attenzione",
    'schedcfg.calendar.syncNever': "non ancora sincronizzata",
    'schedcfg.calendar.lastSync': "Ultima sincronizzazione {ts}",
    'schedcfg.calendar.attentionBanner': "Il tuo calendario ha smesso di sincronizzarsi. Riconnettilo per mantenere accurata la tua disponibilità.",
    'schedcfg.calendar.connect': "Collega {provider}",
    'schedcfg.calendar.reconnect': "Riconnetti {provider}",
    'schedcfg.calendar.disconnect': "Scollega {provider}",
    'schedcfg.calendar.unavailable': "{provider} — non ancora disponibile",
    'schedcfg.calendar.providerOther': "Altro (CalDAV)",
    'schedcfg.feeApplies': 'Si applica una tariffa',
    'schedcfg.confirmationMode': 'Modalità (manual / auto / rule_based)',
    'schedcfg.knowledgeRefsInput': 'ID di riferimento policy (separati da virgole)',
    'schedcfg.requiresDefaults': 'Requisiti predefiniti',
    'schedcfg.remove': 'Rimuovi',
    'schedcfg.mode.label': 'Mode',
    'schedcfg.mode.inPerson': 'In person',
    'schedcfg.mode.audio': 'Audio',
    'schedcfg.mode.video': 'Video',
    'sched.consult.label': 'Consulto',
    'sched.consult.joinAudio': 'Partecipa con audio',
    'sched.consult.joinVideo': 'Partecipa con video',
    'sched.consult.unavailable': 'Non disponibile al momento',
    'sched.consult.joining': 'Connessione…',
    'sched.consult.failed': 'Impossibile partecipare. Riprova.',
    'schedcfg.doc.label': 'Required document',
    'schedcfg.doc.labelPlaceholder': 'e.g. Provincial government-issued ID',
    'schedcfg.doc.labelHelp': 'Name what the patient must provide. Left blank, the app can only say a document is required.',
    'schedcfg.doc.kindDocument': 'PDF or document',
    'schedcfg.doc.kindImage': 'Photo or scan',
    'schedcfg.doc.kindsEmpty': 'At least one kind must be allowed.',
    'schedcfg.day.monday': 'Lunedì',
    'schedcfg.day.tuesday': 'Martedì',
    'schedcfg.day.wednesday': 'Mercoledì',
    'schedcfg.day.thursday': 'Giovedì',
    'schedcfg.day.friday': 'Venerdì',
    'schedcfg.day.saturday': 'Sabato',
    'schedcfg.day.sunday': 'Domenica',
  },

  ja: {
    // ---- D5 appointment reminders (complete; OPEN-05 completed the block that shipped
    // English-only in 1ad4c4b). Terminology reused from the existing catalogue.
    'schedcfg.reminders.title': '予約リマインダー',
    'schedcfg.reminders.enabled': '予約リマインダーを送信する',
    'schedcfg.reminders.offsets': 'リマインドのタイミング（予約前）',
    'schedcfg.reminders.o72': '72時間前',
    'schedcfg.reminders.o24': '24時間前',
    'schedcfg.reminders.o1': '1時間前',
    'schedcfg.reminders.channels': 'リマインダーのチャネル',
    'schedcfg.reminders.chInApp': 'アプリ内',
    'schedcfg.reminders.chPush': 'プッシュ',
    'schedcfg.reminders.chSms': 'SMS',
    'schedcfg.reminders.chEmail': 'メール',
    'schedcfg.reminders.smsUnavailable': 'SMSリマインダーは、ビジネスでSMSが設定され有効になるまでご利用いただけません。',
    'schedcfg.reminders.unavailable': '予約リマインダーは現在のプランに含まれていません。',
    'schedcfg.reminders.addOnRemedy': 'アドオンとしてご利用いただけます。請求と契約 → アドオン をご覧ください。',
    'sched.create.button': '新規予約',
    'sched.create.searchFailed': '顧客を検索できませんでした。もう一度お試しください。',
    'sched.create.title': '新しい予約',
    'sched.create.type': '予約の種類',
    'sched.create.providerFilter': '担当者（任意のフィルター）',
    'sched.create.loadSlots': '空き時間を読み込む',
    'sched.create.loadingSlots': '空き時間を読み込み中…',
    'sched.create.noSlots': '空き時間がありません — 先に空き状況を設定してください。',
    'sched.create.noCrew': '対象スタッフがいません — 後で割り当ててください。',
    'sched.create.pickSlot': '時間を選択',
    'sched.create.customerId': '顧客ID',
    'sched.create.phone': '電話番号',
    'sched.create.email': 'メール',
    'sched.create.customerHint': '顧客ID・電話番号・メールのいずれかを入力してください。サーバーが顧客を特定します。',
    'sched.create.reason': '理由（任意）',
    'sched.create.submit': '予約を作成',
    'sched.create.needType': '予約の種類を選択してください。',
    'sched.create.needCustomer': '顧客ID・電話番号・メールを入力してください。',
    'sched.toast.created': '予約を作成しました',
    'nav.schedulingSettings': 'スケジュール設定',
    'navdesc.schedulingSettings': '営業時間・種類・担当者・ポリシー',
    'page.schedConfig.title': 'スケジュール設定',
    'page.schedConfig.subtitle': '{business} の空き状況・種類・担当者・ポリシーを設定',
    'schedcfg.loading': '設定を読み込み中…',
    'schedcfg.failedLoad': '設定の読み込みに失敗しました',
    'schedcfg.none': 'スケジュール設定がまだありません — 以下で設定してください。',
    'schedcfg.save': '設定を保存',
    'schedcfg.saved': '設定を保存しました',
    'schedcfg.saveFailed': '設定の保存に失敗しました',
    'schedcfg.general': '全般',
    'schedcfg.timezone': 'タイムゾーン',
    'schedcfg.slotGranularity': '枠の粒度（分）',
    'schedcfg.minNotice': '最小通知（分）',
    'schedcfg.maxAdvance': '最大予約可能（日）',
    'schedcfg.autoConfirm': '自動確定ポリシー',
    'schedcfg.autoConfirmOff': 'オフ',
    'schedcfg.autoConfirmRule': 'ルールベース',
    'schedcfg.mode': 'スケジュールモード',
    'schedcfg.industryPack': '業種パックID',
    'schedcfg.workflowTemplate': 'ワークフローテンプレートID',
    'schedcfg.customerLabel': '顧客表示ラベル',
    'schedcfg.resourceLabel': 'リソース表示ラベル',
    'schedcfg.hours': '営業時間',
    'schedcfg.addWindow': '時間帯を追加',
    'schedcfg.closed': '休業',
    'schedcfg.types': '予約の種類',
    'schedcfg.types.inherited': 'These appointment types are set by the tenant’s Industry Profile and are not editable here.',
    'schedcfg.types.srcPack': 'Industry pack',
    'schedcfg.types.srcProfile': 'Industry profile',
    'schedcfg.types.srcTenant': 'Tenant configuration',
    'schedcfg.addType': '種類を追加',
    'schedcfg.typeName': '名称',
    'schedcfg.duration': '所要時間（分）',
    'schedcfg.preparation': '来院前の準備',
    'schedcfg.preparationPlaceholder': 'このサービスを予約する方が事前に行うことは？',
    'schedcfg.preparationHint': 'このサービスの全予約に適用されます。特定の患者について記載しないでください。',
    'schedcfg.buffer': 'バッファ（分）',
    'schedcfg.providers': '担当者',
    'schedcfg.addProvider': '担当者を追加',
    'schedcfg.providerName': '名前',
    'schedcfg.staffId': 'スタッフID',
    'schedcfg.resourceType': 'リソース種別',
    'schedcfg.resourceId': 'リソースID',
    'schedcfg.providerTypes': '種類ID（カンマ区切り、空＝すべて）',
    'schedcfg.blackouts': 'ブロック期間',
    'schedcfg.addBlackout': 'ブロックを追加',
    'schedcfg.start': '開始',
    'schedcfg.end': '終了',
    'schedcfg.reason': '理由',
    'schedcfg.providerOptional': '担当者（任意）',
    'schedcfg.policies': 'ポリシー',
    'schedcfg.calendar.title': "個人カレンダー",
    'schedcfg.calendar.intro': "個人のカレンダーを連携すると、外部の予定が予約可能な時間をブロックします。",
    'schedcfg.calendar.loading': "読み込み中…",
    'schedcfg.calendar.statusLabel': "ステータス:",
    'schedcfg.calendar.statusConnected': "接続済み",
    'schedcfg.calendar.statusAttention': "要対応",
    'schedcfg.calendar.statusNotConnected': "未接続",
    'schedcfg.calendar.syncLabel': "同期:",
    'schedcfg.calendar.syncOk': "最新",
    'schedcfg.calendar.syncStale': "古い状態",
    'schedcfg.calendar.syncError': "要対応",
    'schedcfg.calendar.syncNever': "未同期",
    'schedcfg.calendar.lastSync': "最終同期 {ts}",
    'schedcfg.calendar.attentionBanner': "カレンダーの同期が停止しています。空き時間を正確に保つため、再接続してください。",
    'schedcfg.calendar.connect': "{provider} を接続",
    'schedcfg.calendar.reconnect': "{provider} を再接続",
    'schedcfg.calendar.disconnect': "{provider} を解除",
    'schedcfg.calendar.unavailable': "{provider} — まだ利用できません",
    'schedcfg.calendar.providerOther': "その他 (CalDAV)",
    'schedcfg.feeApplies': '料金が発生',
    'schedcfg.confirmationMode': 'モード（manual / auto / rule_based）',
    'schedcfg.knowledgeRefsInput': 'ポリシー参照ID（カンマ区切り）',
    'schedcfg.requiresDefaults': '既定の要件',
    'schedcfg.remove': '削除',
    'schedcfg.mode.label': 'Mode',
    'schedcfg.mode.inPerson': 'In person',
    'schedcfg.mode.audio': 'Audio',
    'schedcfg.mode.video': 'Video',
    'sched.consult.label': '診察',
    'sched.consult.joinAudio': '音声で参加',
    'sched.consult.joinVideo': 'ビデオで参加',
    'sched.consult.unavailable': '現在ご利用いただけません',
    'sched.consult.joining': '接続しています…',
    'sched.consult.failed': '参加できませんでした。もう一度お試しください。',
    'schedcfg.doc.label': 'Required document',
    'schedcfg.doc.labelPlaceholder': 'e.g. Provincial government-issued ID',
    'schedcfg.doc.labelHelp': 'Name what the patient must provide. Left blank, the app can only say a document is required.',
    'schedcfg.doc.kindDocument': 'PDF or document',
    'schedcfg.doc.kindImage': 'Photo or scan',
    'schedcfg.doc.kindsEmpty': 'At least one kind must be allowed.',
    'schedcfg.day.monday': '月曜日',
    'schedcfg.day.tuesday': '火曜日',
    'schedcfg.day.wednesday': '水曜日',
    'schedcfg.day.thursday': '木曜日',
    'schedcfg.day.friday': '金曜日',
    'schedcfg.day.saturday': '土曜日',
    'schedcfg.day.sunday': '日曜日',
  },

  ko: {
    // ---- D5 appointment reminders (complete; OPEN-05 completed the block that shipped
    // English-only in 1ad4c4b). Terminology reused from the existing catalogue.
    'schedcfg.reminders.title': '예약 알림',
    'schedcfg.reminders.enabled': '예약 알림 보내기',
    'schedcfg.reminders.offsets': '알림 시점 (예약 전)',
    'schedcfg.reminders.o72': '72시간 전',
    'schedcfg.reminders.o24': '24시간 전',
    'schedcfg.reminders.o1': '1시간 전',
    'schedcfg.reminders.channels': '알림 채널',
    'schedcfg.reminders.chInApp': '앱 내',
    'schedcfg.reminders.chPush': '푸시',
    'schedcfg.reminders.chSms': 'SMS',
    'schedcfg.reminders.chEmail': '이메일',
    'schedcfg.reminders.smsUnavailable': 'SMS 알림은 비즈니스에 SMS가 구성되고 활성화될 때까지 사용할 수 없습니다.',
    'schedcfg.reminders.unavailable': '예약 알림은 현재 요금제에 포함되어 있지 않습니다.',
    'schedcfg.reminders.addOnRemedy': '애드온으로 이용할 수 있습니다. 결제 및 구독 → 애드온을 확인하세요.',
    'sched.create.button': '새 예약',
    'sched.create.searchFailed': '고객을 검색할 수 없습니다. 다시 시도해 주세요.',
    'sched.create.title': '새 예약',
    'sched.create.type': '예약 유형',
    'sched.create.providerFilter': '담당자(선택 필터)',
    'sched.create.loadSlots': '이용 가능 시간 불러오기',
    'sched.create.loadingSlots': '이용 가능 시간을 불러오는 중…',
    'sched.create.noSlots': '이용 가능한 시간이 없습니다 — 먼저 이용 가능 시간을 설정하세요.',
    'sched.create.noCrew': '배정 가능한 직원이 없습니다 — 나중에 배정하세요.',
    'sched.create.pickSlot': '시간 선택',
    'sched.create.customerId': '고객 ID',
    'sched.create.phone': '전화번호',
    'sched.create.email': '이메일',
    'sched.create.customerHint': '고객 ID, 전화번호 또는 이메일 중 하나 이상 입력하세요. 서버가 고객을 식별합니다.',
    'sched.create.reason': '사유(선택)',
    'sched.create.submit': '예약 생성',
    'sched.create.needType': '예약 유형을 선택하세요.',
    'sched.create.needCustomer': '고객 ID, 전화번호 또는 이메일을 입력하세요.',
    'sched.toast.created': '예약이 생성되었습니다',
    'nav.schedulingSettings': '일정 설정',
    'navdesc.schedulingSettings': '영업시간, 유형, 담당자, 정책',
    'page.schedConfig.title': '일정 설정',
    'page.schedConfig.subtitle': '{business}의 이용 가능 시간, 유형, 담당자 및 정책을 설정하세요',
    'schedcfg.loading': '설정을 불러오는 중…',
    'schedcfg.failedLoad': '설정을 불러오지 못했습니다',
    'schedcfg.none': '아직 일정 설정이 없습니다 — 아래에서 설정하세요.',
    'schedcfg.save': '설정 저장',
    'schedcfg.saved': '설정이 저장되었습니다',
    'schedcfg.saveFailed': '설정을 저장하지 못했습니다',
    'schedcfg.general': '일반',
    'schedcfg.timezone': '시간대',
    'schedcfg.slotGranularity': '슬롯 단위(분)',
    'schedcfg.minNotice': '최소 통지(분)',
    'schedcfg.maxAdvance': '최대 예약 가능(일)',
    'schedcfg.autoConfirm': '자동 확정 정책',
    'schedcfg.autoConfirmOff': '끔',
    'schedcfg.autoConfirmRule': '규칙 기반',
    'schedcfg.mode': '일정 모드',
    'schedcfg.industryPack': '업종 팩 ID',
    'schedcfg.workflowTemplate': '워크플로 템플릿 ID',
    'schedcfg.customerLabel': '고객 표시 라벨',
    'schedcfg.resourceLabel': '리소스 표시 라벨',
    'schedcfg.hours': '영업시간',
    'schedcfg.addWindow': '시간대 추가',
    'schedcfg.closed': '휴무',
    'schedcfg.types': '예약 유형',
    'schedcfg.types.inherited': 'These appointment types are set by the tenant’s Industry Profile and are not editable here.',
    'schedcfg.types.srcPack': 'Industry pack',
    'schedcfg.types.srcProfile': 'Industry profile',
    'schedcfg.types.srcTenant': 'Tenant configuration',
    'schedcfg.addType': '유형 추가',
    'schedcfg.typeName': '이름',
    'schedcfg.duration': '소요 시간(분)',
    'schedcfg.preparation': '방문 전 준비사항',
    'schedcfg.preparationPlaceholder': '이 서비스를 예약하는 분이 미리 해야 할 일은?',
    'schedcfg.preparationHint': '이 서비스의 모든 예약에 적용됩니다. 특정 환자에 대해 작성하지 마세요.',
    'schedcfg.buffer': '버퍼(분)',
    'schedcfg.providers': '담당자',
    'schedcfg.addProvider': '담당자 추가',
    'schedcfg.providerName': '이름',
    'schedcfg.staffId': '직원 ID',
    'schedcfg.resourceType': '리소스 유형',
    'schedcfg.resourceId': '리소스 ID',
    'schedcfg.providerTypes': '유형 ID(쉼표로 구분, 비어 있으면 전체)',
    'schedcfg.blackouts': '차단 기간',
    'schedcfg.addBlackout': '차단 추가',
    'schedcfg.start': '시작',
    'schedcfg.end': '종료',
    'schedcfg.reason': '사유',
    'schedcfg.providerOptional': '담당자(선택)',
    'schedcfg.policies': '정책',
    'schedcfg.calendar.title': "개인 캘린더",
    'schedcfg.calendar.intro': "개인 캘린더를 연결하면 외부 일정이 예약 가능한 시간을 차단합니다.",
    'schedcfg.calendar.loading': "불러오는 중…",
    'schedcfg.calendar.statusLabel': "상태:",
    'schedcfg.calendar.statusConnected': "연결됨",
    'schedcfg.calendar.statusAttention': "조치 필요",
    'schedcfg.calendar.statusNotConnected': "연결되지 않음",
    'schedcfg.calendar.syncLabel': "동기화:",
    'schedcfg.calendar.syncOk': "최신",
    'schedcfg.calendar.syncStale': "오래됨",
    'schedcfg.calendar.syncError': "조치 필요",
    'schedcfg.calendar.syncNever': "아직 동기화되지 않음",
    'schedcfg.calendar.lastSync': "마지막 동기화 {ts}",
    'schedcfg.calendar.attentionBanner': "캘린더 동기화가 중단되었습니다. 예약 가능 시간을 정확하게 유지하려면 다시 연결하세요.",
    'schedcfg.calendar.connect': "{provider} 연결",
    'schedcfg.calendar.reconnect': "{provider} 다시 연결",
    'schedcfg.calendar.disconnect': "{provider} 연결 해제",
    'schedcfg.calendar.unavailable': "{provider} — 아직 사용할 수 없음",
    'schedcfg.calendar.providerOther': "기타 (CalDAV)",
    'schedcfg.feeApplies': '수수료 부과',
    'schedcfg.confirmationMode': '모드(manual / auto / rule_based)',
    'schedcfg.knowledgeRefsInput': '정책 참조 ID(쉼표로 구분)',
    'schedcfg.requiresDefaults': '기본 요구 사항',
    'schedcfg.remove': '제거',
    'schedcfg.mode.label': 'Mode',
    'schedcfg.mode.inPerson': 'In person',
    'schedcfg.mode.audio': 'Audio',
    'schedcfg.mode.video': 'Video',
    'sched.consult.label': '상담',
    'sched.consult.joinAudio': '음성으로 참여',
    'sched.consult.joinVideo': '화상으로 참여',
    'sched.consult.unavailable': '지금은 이용할 수 없습니다',
    'sched.consult.joining': '연결 중…',
    'sched.consult.failed': '참여하지 못했습니다. 다시 시도해 주세요.',
    'schedcfg.doc.label': 'Required document',
    'schedcfg.doc.labelPlaceholder': 'e.g. Provincial government-issued ID',
    'schedcfg.doc.labelHelp': 'Name what the patient must provide. Left blank, the app can only say a document is required.',
    'schedcfg.doc.kindDocument': 'PDF or document',
    'schedcfg.doc.kindImage': 'Photo or scan',
    'schedcfg.doc.kindsEmpty': 'At least one kind must be allowed.',
    'schedcfg.day.monday': '월요일',
    'schedcfg.day.tuesday': '화요일',
    'schedcfg.day.wednesday': '수요일',
    'schedcfg.day.thursday': '목요일',
    'schedcfg.day.friday': '금요일',
    'schedcfg.day.saturday': '토요일',
    'schedcfg.day.sunday': '일요일',
  },

  zh: {
    // ---- D5 appointment reminders (complete; OPEN-05 completed the block that shipped
    // English-only in 1ad4c4b). Terminology reused from the existing catalogue.
    'schedcfg.reminders.title': '预约提醒',
    'schedcfg.reminders.enabled': '发送预约提醒',
    'schedcfg.reminders.offsets': '提醒时间（预约前）',
    'schedcfg.reminders.o72': '提前 72 小时',
    'schedcfg.reminders.o24': '提前 24 小时',
    'schedcfg.reminders.o1': '提前 1 小时',
    'schedcfg.reminders.channels': '提醒渠道',
    'schedcfg.reminders.chInApp': '应用内',
    'schedcfg.reminders.chPush': '推送',
    'schedcfg.reminders.chSms': '短信',
    'schedcfg.reminders.chEmail': '邮件',
    'schedcfg.reminders.smsUnavailable': '在为您的商家配置并启用短信之前，短信提醒不可用。',
    'schedcfg.reminders.unavailable': '当前套餐不包含预约提醒。',
    'schedcfg.reminders.addOnRemedy': '可作为附加组件购买：请查看“账单与订阅 → 附加组件”。',
    'sched.create.button': '新建预约',
    'sched.create.searchFailed': '无法搜索客户，请重试。',
    'sched.create.title': '新预约',
    'sched.create.type': '预约类型',
    'sched.create.providerFilter': '服务人员（可选筛选）',
    'sched.create.loadSlots': '加载可用时间',
    'sched.create.loadingSlots': '正在加载可用时间…',
    'sched.create.noSlots': '没有可用时间 — 请先配置可用性。',
    'sched.create.noCrew': '暂无可用员工 — 稍后分配。',
    'sched.create.pickSlot': '选择时间',
    'sched.create.customerId': '客户 ID',
    'sched.create.phone': '电话',
    'sched.create.email': '邮箱',
    'sched.create.customerHint': '至少提供一项：客户 ID、电话或邮箱。服务器会识别客户。',
    'sched.create.reason': '原因（可选）',
    'sched.create.submit': '创建预约',
    'sched.create.needType': '请选择预约类型。',
    'sched.create.needCustomer': '请提供客户 ID、电话或邮箱。',
    'sched.toast.created': '预约已创建',
    'nav.schedulingSettings': '日程设置',
    'navdesc.schedulingSettings': '营业时间、类型、服务人员、政策',
    'page.schedConfig.title': '日程设置',
    'page.schedConfig.subtitle': '为 {business} 配置可用性、类型、服务人员和政策',
    'schedcfg.loading': '正在加载配置…',
    'schedcfg.failedLoad': '加载配置失败',
    'schedcfg.none': '尚无日程配置 — 请在下方设置。',
    'schedcfg.save': '保存配置',
    'schedcfg.saved': '配置已保存',
    'schedcfg.saveFailed': '保存配置失败',
    'schedcfg.general': '常规',
    'schedcfg.timezone': '时区',
    'schedcfg.slotGranularity': '时段粒度（分钟）',
    'schedcfg.minNotice': '最短提前（分钟）',
    'schedcfg.maxAdvance': '最长提前（天）',
    'schedcfg.autoConfirm': '自动确认政策',
    'schedcfg.autoConfirmOff': '关闭',
    'schedcfg.autoConfirmRule': '基于规则',
    'schedcfg.mode': '排期模式',
    'schedcfg.industryPack': '行业包 ID',
    'schedcfg.workflowTemplate': '工作流模板 ID',
    'schedcfg.customerLabel': '客户显示标签',
    'schedcfg.resourceLabel': '资源显示标签',
    'schedcfg.hours': '营业时间',
    'schedcfg.addWindow': '添加时间段',
    'schedcfg.closed': '休息',
    'schedcfg.types': '预约类型',
    'schedcfg.types.inherited': 'These appointment types are set by the tenant’s Industry Profile and are not editable here.',
    'schedcfg.types.srcPack': 'Industry pack',
    'schedcfg.types.srcProfile': 'Industry profile',
    'schedcfg.types.srcTenant': 'Tenant configuration',
    'schedcfg.addType': '添加类型',
    'schedcfg.typeName': '名称',
    'schedcfg.duration': '时长（分钟）',
    'schedcfg.preparation': '就诊前准备',
    'schedcfg.preparationPlaceholder': '预约此服务的人事先需要做什么？',
    'schedcfg.preparationHint': '适用于此服务的所有预约，请勿填写特定患者的信息。',
    'schedcfg.buffer': '缓冲（分钟）',
    'schedcfg.providers': '服务人员',
    'schedcfg.addProvider': '添加服务人员',
    'schedcfg.providerName': '姓名',
    'schedcfg.staffId': '员工 ID',
    'schedcfg.resourceType': '资源类型',
    'schedcfg.resourceId': '资源 ID',
    'schedcfg.providerTypes': '类型 ID（逗号分隔；留空 = 全部）',
    'schedcfg.blackouts': '封锁时段',
    'schedcfg.addBlackout': '添加封锁',
    'schedcfg.start': '开始',
    'schedcfg.end': '结束',
    'schedcfg.reason': '原因',
    'schedcfg.providerOptional': '服务人员（可选）',
    'schedcfg.policies': '政策',
    'schedcfg.calendar.title': "个人日历",
    'schedcfg.calendar.intro': "连接您的日历，外部安排将自动占用可预约时段。",
    'schedcfg.calendar.loading': "加载中…",
    'schedcfg.calendar.statusLabel': "状态：",
    'schedcfg.calendar.statusConnected': "已连接",
    'schedcfg.calendar.statusAttention': "需要处理",
    'schedcfg.calendar.statusNotConnected': "未连接",
    'schedcfg.calendar.syncLabel': "同步：",
    'schedcfg.calendar.syncOk': "已是最新",
    'schedcfg.calendar.syncStale': "已过期",
    'schedcfg.calendar.syncError': "需要处理",
    'schedcfg.calendar.syncNever': "尚未同步",
    'schedcfg.calendar.lastSync': "上次同步 {ts}",
    'schedcfg.calendar.attentionBanner': "您的日历已停止同步。请重新连接以保持可预约时间准确。",
    'schedcfg.calendar.connect': "连接 {provider}",
    'schedcfg.calendar.reconnect': "重新连接 {provider}",
    'schedcfg.calendar.disconnect': "断开 {provider}",
    'schedcfg.calendar.unavailable': "{provider} — 尚未提供",
    'schedcfg.calendar.providerOther': "其他 (CalDAV)",
    'schedcfg.feeApplies': '收取费用',
    'schedcfg.confirmationMode': '模式（manual / auto / rule_based）',
    'schedcfg.knowledgeRefsInput': '政策参考 ID（逗号分隔）',
    'schedcfg.requiresDefaults': '默认要求',
    'schedcfg.remove': '移除',
    'schedcfg.mode.label': 'Mode',
    'schedcfg.mode.inPerson': 'In person',
    'schedcfg.mode.audio': 'Audio',
    'schedcfg.mode.video': 'Video',
    'sched.consult.label': '问诊',
    'sched.consult.joinAudio': '加入语音',
    'sched.consult.joinVideo': '加入视频',
    'sched.consult.unavailable': '当前不可用',
    'sched.consult.joining': '正在连接…',
    'sched.consult.failed': '无法加入，请重试。',
    'schedcfg.doc.label': 'Required document',
    'schedcfg.doc.labelPlaceholder': 'e.g. Provincial government-issued ID',
    'schedcfg.doc.labelHelp': 'Name what the patient must provide. Left blank, the app can only say a document is required.',
    'schedcfg.doc.kindDocument': 'PDF or document',
    'schedcfg.doc.kindImage': 'Photo or scan',
    'schedcfg.doc.kindsEmpty': 'At least one kind must be allowed.',
    'schedcfg.day.monday': '周一',
    'schedcfg.day.tuesday': '周二',
    'schedcfg.day.wednesday': '周三',
    'schedcfg.day.thursday': '周四',
    'schedcfg.day.friday': '周五',
    'schedcfg.day.saturday': '周六',
    'schedcfg.day.sunday': '周日',
  },

  ar: {
    // ---- D5 appointment reminders (complete; OPEN-05 completed the block that shipped
    // English-only in 1ad4c4b). Terminology reused from the existing catalogue.
    'schedcfg.reminders.title': 'تذكيرات المواعيد',
    'schedcfg.reminders.enabled': 'إرسال تذكيرات المواعيد',
    'schedcfg.reminders.offsets': 'وقت التذكير (قبل الموعد)',
    'schedcfg.reminders.o72': 'قبل 72 ساعة',
    'schedcfg.reminders.o24': 'قبل 24 ساعة',
    'schedcfg.reminders.o1': 'قبل ساعة واحدة',
    'schedcfg.reminders.channels': 'قنوات التذكير',
    'schedcfg.reminders.chInApp': 'داخل التطبيق',
    'schedcfg.reminders.chPush': 'إشعار فوري',
    'schedcfg.reminders.chSms': 'رسالة نصية (SMS)',
    'schedcfg.reminders.chEmail': 'بريد إلكتروني',
    'schedcfg.reminders.smsUnavailable': 'تذكيرات الرسائل النصية غير متاحة حتى يتم إعداد الرسائل النصية وتفعيلها لشركتك.',
    'schedcfg.reminders.unavailable': 'تذكيرات المواعيد غير مضمَّنة في خطتك الحالية.',
    'schedcfg.reminders.addOnRemedy': 'تتوفر كإضافة — راجع الفوترة والاشتراك ← الإضافات.',
    'sched.create.button': 'حجز جديد',
    'sched.create.searchFailed': 'تعذّر البحث عن العملاء. حاول مرة أخرى.',
    'sched.create.title': 'موعد جديد',
    'sched.create.type': 'نوع الموعد',
    'sched.create.providerFilter': 'مقدم الخدمة (تصفية اختيارية)',
    'sched.create.loadSlots': 'تحميل الأوقات المتاحة',
    'sched.create.loadingSlots': 'جارٍ تحميل الأوقات المتاحة…',
    'sched.create.noSlots': 'لا توجد أوقات متاحة — قم بإعداد التوفر أولاً.',
    'sched.create.noCrew': 'لا يوجد موظفون مؤهلون — عيّن لاحقًا.',
    'sched.create.pickSlot': 'اختر وقتًا',
    'sched.create.customerId': 'معرّف العميل',
    'sched.create.phone': 'الهاتف',
    'sched.create.email': 'البريد الإلكتروني',
    'sched.create.customerHint': 'قدّم واحدًا على الأقل: معرّف العميل أو الهاتف أو البريد. يقوم الخادم بتحديد العميل.',
    'sched.create.reason': 'السبب (اختياري)',
    'sched.create.submit': 'إنشاء الحجز',
    'sched.create.needType': 'اختر نوع الموعد.',
    'sched.create.needCustomer': 'قدّم معرّف العميل أو الهاتف أو البريد.',
    'sched.toast.created': 'تم إنشاء الموعد',
    'nav.schedulingSettings': 'إعدادات الجدولة',
    'navdesc.schedulingSettings': 'الساعات والأنواع ومقدمو الخدمة والسياسات',
    'page.schedConfig.title': 'إعدادات الجدولة',
    'page.schedConfig.subtitle': 'إعداد التوفر والأنواع ومقدمي الخدمة والسياسات لـ {business}',
    'schedcfg.loading': 'جارٍ تحميل الإعدادات…',
    'schedcfg.failedLoad': 'فشل تحميل الإعدادات',
    'schedcfg.none': 'لا توجد إعدادات جدولة بعد — قم بإعدادها أدناه.',
    'schedcfg.save': 'حفظ الإعدادات',
    'schedcfg.saved': 'تم حفظ الإعدادات',
    'schedcfg.saveFailed': 'فشل حفظ الإعدادات',
    'schedcfg.general': 'عام',
    'schedcfg.timezone': 'المنطقة الزمنية',
    'schedcfg.slotGranularity': 'دقة الفترات (دقيقة)',
    'schedcfg.minNotice': 'أدنى إشعار (دقيقة)',
    'schedcfg.maxAdvance': 'أقصى حجز مسبق (أيام)',
    'schedcfg.autoConfirm': 'سياسة التأكيد التلقائي',
    'schedcfg.autoConfirmOff': 'إيقاف',
    'schedcfg.autoConfirmRule': 'قائمة على القواعد',
    'schedcfg.mode': 'وضع الجدولة',
    'schedcfg.industryPack': 'معرّف حزمة القطاع',
    'schedcfg.workflowTemplate': 'معرّف قالب سير العمل',
    'schedcfg.customerLabel': 'تسمية عرض العميل',
    'schedcfg.resourceLabel': 'تسمية عرض المورد',
    'schedcfg.hours': 'ساعات العمل',
    'schedcfg.addWindow': 'إضافة فترة',
    'schedcfg.closed': 'مغلق',
    'schedcfg.types': 'أنواع المواعيد',
    'schedcfg.types.inherited': 'These appointment types are set by the tenant’s Industry Profile and are not editable here.',
    'schedcfg.types.srcPack': 'Industry pack',
    'schedcfg.types.srcProfile': 'Industry profile',
    'schedcfg.types.srcTenant': 'Tenant configuration',
    'schedcfg.addType': 'إضافة نوع',
    'schedcfg.typeName': 'الاسم',
    'schedcfg.duration': 'المدة (دقيقة)',
    'schedcfg.preparation': 'التحضير قبل الزيارة',
    'schedcfg.preparationPlaceholder': 'ما الذي ينبغي على كل من يحجز هذه الخدمة فعله مسبقًا؟',
    'schedcfg.preparationHint': 'ينطبق على جميع حجوزات هذه الخدمة — لا تكتب أبدًا عن مريض بعينه.',
    'schedcfg.buffer': 'فاصل (دقيقة)',
    'schedcfg.providers': 'مقدمو الخدمة',
    'schedcfg.addProvider': 'إضافة مقدم خدمة',
    'schedcfg.providerName': 'الاسم',
    'schedcfg.staffId': 'معرّف الموظف',
    'schedcfg.resourceType': 'نوع المورد',
    'schedcfg.resourceId': 'معرّف المورد',
    'schedcfg.providerTypes': 'معرّفات الأنواع (مفصولة بفواصل؛ فارغ = الكل)',
    'schedcfg.blackouts': 'فترات الحظر',
    'schedcfg.addBlackout': 'إضافة حظر',
    'schedcfg.start': 'البداية',
    'schedcfg.end': 'النهاية',
    'schedcfg.reason': 'السبب',
    'schedcfg.providerOptional': 'مقدم الخدمة (اختياري)',
    'schedcfg.policies': 'السياسات',
    'schedcfg.calendar.title': "التقويم الشخصي",
    'schedcfg.calendar.intro': "اربط تقويمك الشخصي ليحجب ارتباطاتك الخارجية أوقاتك المتاحة للحجز.",
    'schedcfg.calendar.loading': "جارٍ التحميل…",
    'schedcfg.calendar.statusLabel': "الحالة:",
    'schedcfg.calendar.statusConnected': "متصل",
    'schedcfg.calendar.statusAttention': "يتطلب انتباهًا",
    'schedcfg.calendar.statusNotConnected': "غير متصل",
    'schedcfg.calendar.syncLabel': "المزامنة:",
    'schedcfg.calendar.syncOk': "محدَّثة",
    'schedcfg.calendar.syncStale': "غير محدَّثة",
    'schedcfg.calendar.syncError': "يتطلب انتباهًا",
    'schedcfg.calendar.syncNever': "لم تتم المزامنة بعد",
    'schedcfg.calendar.lastSync': "آخر مزامنة {ts}",
    'schedcfg.calendar.attentionBanner': "توقفت مزامنة تقويمك. أعد الاتصال للحفاظ على دقة أوقاتك المتاحة.",
    'schedcfg.calendar.connect': "توصيل {provider}",
    'schedcfg.calendar.reconnect': "إعادة توصيل {provider}",
    'schedcfg.calendar.disconnect': "فصل {provider}",
    'schedcfg.calendar.unavailable': "{provider} — غير متاح بعد",
    'schedcfg.calendar.providerOther': "أخرى (CalDAV)",
    'schedcfg.feeApplies': 'تُطبَّق رسوم',
    'schedcfg.confirmationMode': 'الوضع (manual / auto / rule_based)',
    'schedcfg.knowledgeRefsInput': 'معرّفات مراجع السياسة (مفصولة بفواصل)',
    'schedcfg.requiresDefaults': 'المتطلبات الافتراضية',
    'schedcfg.remove': 'إزالة',
    'schedcfg.mode.label': 'Mode',
    'schedcfg.mode.inPerson': 'In person',
    'schedcfg.mode.audio': 'Audio',
    'schedcfg.mode.video': 'Video',
    'sched.consult.label': 'الاستشارة',
    'sched.consult.joinAudio': 'الانضمام بالصوت',
    'sched.consult.joinVideo': 'الانضمام بالفيديو',
    'sched.consult.unavailable': 'غير متاح حالياً',
    'sched.consult.joining': 'جارٍ الاتصال…',
    'sched.consult.failed': 'تعذّر الانضمام. يرجى المحاولة مرة أخرى.',
    'schedcfg.doc.label': 'Required document',
    'schedcfg.doc.labelPlaceholder': 'e.g. Provincial government-issued ID',
    'schedcfg.doc.labelHelp': 'Name what the patient must provide. Left blank, the app can only say a document is required.',
    'schedcfg.doc.kindDocument': 'PDF or document',
    'schedcfg.doc.kindImage': 'Photo or scan',
    'schedcfg.doc.kindsEmpty': 'At least one kind must be allowed.',
    'schedcfg.day.monday': 'الاثنين',
    'schedcfg.day.tuesday': 'الثلاثاء',
    'schedcfg.day.wednesday': 'الأربعاء',
    'schedcfg.day.thursday': 'الخميس',
    'schedcfg.day.friday': 'الجمعة',
    'schedcfg.day.saturday': 'السبت',
    'schedcfg.day.sunday': 'الأحد',
  },
};

// ---- display-name parity keys (list/detail names + Technical Details) -------------------------
// Merged into each language block below so the shared per-language lookup resolves them. English
// fallback applies for any language missing a key.
const _schedDisplayExtra = {
  en: { 'sched.business': 'Business', 'sched.typeFallback': 'Appointment', 'sched.technicalDetails': 'Technical Details' },
  es: { 'sched.business': 'Negocio', 'sched.typeFallback': 'Cita', 'sched.technicalDetails': 'Detalles técnicos' },
  fr: { 'sched.business': 'Entreprise', 'sched.typeFallback': 'Rendez-vous', 'sched.technicalDetails': 'Détails techniques' },
  pt: { 'sched.business': 'Empresa', 'sched.typeFallback': 'Compromisso', 'sched.technicalDetails': 'Detalhes técnicos' },
  de: { 'sched.business': 'Unternehmen', 'sched.typeFallback': 'Termin', 'sched.technicalDetails': 'Technische Details' },
  it: { 'sched.business': 'Azienda', 'sched.typeFallback': 'Appuntamento', 'sched.technicalDetails': 'Dettagli tecnici' },
  ja: { 'sched.business': 'ビジネス', 'sched.typeFallback': '予約', 'sched.technicalDetails': '技術的詳細' },
  ko: { 'sched.business': '비즈니스', 'sched.typeFallback': '예약', 'sched.technicalDetails': '기술 세부정보' },
  zh: { 'sched.business': '商家', 'sched.typeFallback': '预约', 'sched.technicalDetails': '技术详情' },
  ar: { 'sched.business': 'الشركة', 'sched.typeFallback': 'موعد', 'sched.technicalDetails': 'التفاصيل الفنية' },
};
Object.keys(_schedDisplayExtra).forEach((lang) => {
  schedulingExtraTranslations[lang] = Object.assign(
    schedulingExtraTranslations[lang] || {},
    _schedDisplayExtra[lang],
  );
});

export default schedulingExtraTranslations;
